/* eslint-disable max-lines */
import { z } from "zod"

import type { ChatSessionModel } from "./models"

import { BACKEND_URI } from "@/constants/config"
import { api } from "@/lib/api-client"
import { logger } from "@/lib/logger"
import { SettingsService } from "@/services/settings-service"
import {
  TENDER_ADMIN_MODE_HEADER,
  tenderAdminModeHeaderValue,
} from "@/stores/tender-admin-mode-store"

import { fetchWithAuthRetry, getHeaders } from "./api"

const BASE = "/tender"

/**
 * Headers for the direct-fetch / download tender endpoints (the ones that bypass
 * the Axios `api` client and its interceptor). Adds the per-browser admin-mode
 * opt-in alongside the standard auth headers so these paths gate the same way as
 * the Axios path. See `tender-admin-mode-store` and `tender_admin_mode_active`.
 */
function getTenderHeaders(token: string | undefined): Record<string, string> {
  return { ...getHeaders(token), [TENDER_ADMIN_MODE_HEADER]: tenderAdminModeHeaderValue() }
}

/**
 * Parse an array while silently dropping items that fail individual validation.
 * Prevents a single malformed item from breaking the entire extraction parse.
 */
function safeArrayOf<T extends z.ZodTypeAny>(schema: T, label?: string) {
  return z.preprocess((val) => {
    if (!Array.isArray(val)) return []
    return val.filter((item) => {
      const result = schema.safeParse(item)
      if (!result.success && label) {
        logger.warn(`[safeArrayOf:${label}] Dropped item:`, result.error.issues)
      }
      return result.success
    })
  }, z.array(schema))
}

// ── Case schemas ──────────────────────────────────────────────────────────────

const DocumentStatusSchema = z.enum(["uppladdad", "indexeras", "indexerad", "fel"])

const TenderDocumentSchema = z.object({
  id: z.string(),
  filename: z.string(),
  uploadedAt: z.string(),
  status: DocumentStatusSchema,
  pages: z.number(),
  sizeKb: z.number(),
})

const TenderRunSchema = z.object({
  id: z.string(),
  runNumber: z.number(),
  createdAt: z.string(),
  procurementDocuments: z.array(TenderDocumentSchema),
  contextDocuments: z.array(TenderDocumentSchema),
  bidDocuments: z.array(TenderDocumentSchema).optional().default([]),
  cvDocuments: z.array(TenderDocumentSchema).optional().default([]),
  requirements: z.array(z.unknown()),
  evaluationCriteria: z.array(z.unknown()),
  contractTerms: z.array(z.unknown()),
  criticalDates: z.array(z.unknown()),
  requirementMatrix: z.array(z.unknown()),
  risks: z.array(z.unknown()),
  scorecardDimensions: z.array(z.unknown()),
  roleRequirements: z.array(z.unknown()),
  bidDeliverables: z.array(z.unknown()),
  bidSummary: z.unknown().nullable().optional(),
  recommendation: z.unknown().nullable(),
  overrideDocumentIds: z.array(z.string()).optional().default([]),
  extractionStatus: z.string().nullable().optional(),
  bidReviewStatus: z.string().nullable().optional(),
  cvMatchStatus: z.string().nullable().optional(),
  autoPipelineStatus: z.string().nullable().optional(),
  autoPipelineExpectedDocuments: z.number().nullable().optional(),
})

const TENDER_CASE_STATUSES = ["utkast", "under_analys", "analyserad", "go", "no_go"] as const

/**
 * Case status, tolerant of values this build does not know yet (for example one
 * written by a newer backend). An unknown status falls back to "utkast": it
 * implies no Go/No-Go decision, so the case still lists and opens and its
 * display status is derived from the active run. The raw value is logged so the
 * mismatch is visible instead of silently hiding the case.
 */
const TenderCaseStatusSchema = z.enum(TENDER_CASE_STATUSES).catch((ctx) => {
  logger.warn(`[tender] Unknown case status ${JSON.stringify(ctx.value)}; showing it as "utkast"`)
  return "utkast"
})

const TenderParticipantRoleSchema = z.enum(["owner", "admin", "editor", "reader"])

// Why the caller can access a case. "viewer" = open read-only access (not
// involved): cases are open by default, so anyone can view, but only owner /
// participant / admin_default sources can edit. "viewer" callers must self-join.
const TenderAccessSourceSchema = z.enum(["owner", "participant", "admin_default", "viewer"])

const TenderInvitationNotificationSchema = z.object({
  invitedBy: z.string().optional(),
  invitedByDisplayName: z.string().optional(),
  role: TenderParticipantRoleSchema.optional(),
})

const TenderCaseSchema = z.object({
  id: z.string(),
  title: z.string(),
  isTitleAutoGenerated: z.boolean(),
  reference: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
  submissionDeadline: z.string().nullable().optional(),
  submissionDeadlineIsManual: z.boolean().optional(),
  questionsDeadline: z.string().nullable().optional(),
  procuringAuthority: z.string().nullable().optional(),
  status: TenderCaseStatusSchema,
  activeRunId: z.string(),
  ownerUserId: z.string().optional(),
  sourceDocumentsUrl: z.string().nullable().optional(),
  responsibleUserOid: z.string().nullable().optional(),
  responsibleDisplayName: z.string().optional(),
  responsibleEmail: z.string().optional(),
  handedOffAt: z.string().nullable().optional(),
  handedOffBy: z.string().nullable().optional(),
  handedOffTo: z.string().nullable().optional(),
  currentUserRole: TenderParticipantRoleSchema.optional(),
  currentUserAccessSource: TenderAccessSourceSchema.optional(),
  hasUnviewedNotification: z.boolean().optional(),
  invitationNotification: TenderInvitationNotificationSchema.optional(),
  runs: z.array(TenderRunSchema),
})

export type TenderParticipantRole = z.infer<typeof TenderParticipantRoleSchema>
export type TenderAccessSource = z.infer<typeof TenderAccessSourceSchema>

/** The intended Ansvarig / a deltagare, as sent to the case routes. */
export type TenderPersonRef = {
  oid: string
  displayName: string
  /** Carried so hand-over can pre-fill a recipient it can actually send to. */
  email?: string
}

export type TenderParticipantRef = TenderPersonRef & {
  role?: Exclude<TenderParticipantRole, "owner">
}

export type TenderParticipant = {
  oid: string
  displayName: string
  type: "user" | "group"
  role: TenderParticipantRole
}

export type TenderParticipantsResponse = {
  participants: TenderParticipant[]
  ownerUserId: string
}

export type TenderCaseApi = z.infer<typeof TenderCaseSchema>
export type TenderDocumentApi = z.infer<typeof TenderDocumentSchema>
export type TenderRunApi = z.infer<typeof TenderRunSchema>

// ── Landing-page list summary ─────────────────────────────────────────────────
// Lightweight shape returned by GET /api/tender/cases — contains only the fields
// the list table needs. The full TenderCaseApi shape is only used on case detail.

export const TenderCaseListItemSchema = z.object({
  id: z.string(),
  title: z.string(),
  createdAt: z.string(),
  status: TenderCaseStatusSchema,
  activeRunId: z.string(),
  activeRunNumber: z.number().nullable(),
  extractionStatus: z.string().nullable().optional(),
  cvMatchStatus: z.string().nullable().optional(),
  bidReviewStatus: z.string().nullable().optional(),
  autoPipelineStatus: z.string().nullable().optional(),
  autoPipelineExpectedDocuments: z.number().nullable().optional(),
  documentCount: z.number(),
  submissionDeadline: z.string().nullable().optional(),
  submissionDeadlineIsManual: z.boolean().optional(),
  questionsDeadline: z.string().nullable().optional(),
  lastActivityAt: z.string(),
  bidReviewScore: z.number().nullable().optional(),
  bidReviewGoNoGo: z.string().nullable().optional(),
  ownerUserId: z.string().optional(),
  ownerDisplayName: z.string().optional(),
  responsibleUserOid: z.string().nullable().optional(),
  responsibleDisplayName: z.string().optional(),
  responsibleEmail: z.string().optional(),
  handedOffAt: z.string().nullable().optional(),
  handedOffTo: z.string().nullable().optional(),
  currentUserRole: TenderParticipantRoleSchema.optional(),
  currentUserAccessSource: TenderAccessSourceSchema.optional(),
  hasUnviewedNotification: z.boolean().optional(),
  invitationNotification: TenderInvitationNotificationSchema.optional(),
  hasFailedSubcategories: z.boolean().optional(),
})

export type TenderCaseListItemApi = z.infer<typeof TenderCaseListItemSchema>

// ── Extraction schemas ────────────────────────────────────────────────────────

export const ExtractionCategoryStatusSchema = z.enum([
  "pending",
  "extracting",
  "verifying",
  "completed",
  "failed",
  "skipped",
])

// documentName can be an array in legacy Cosmos data where the LLM returned a list —
// coerce it to a string so old saved extractions don't drop entire items.
const documentNameSchema = z
  .union([z.string(), z.array(z.string())])
  .transform((v) => (Array.isArray(v) ? (v[0] ?? "") : v))

const SourceRefSchema = z.object({
  kind: z.enum(["file", "library"]),
  documentId: z.string().nullish(),
  documentName: documentNameSchema,
  page: z.number().optional(),
  libraryId: z.string().nullish(),
  libraryName: z.string().nullish(),
})

const SourceSnippetSchema = z.object({
  kind: z.enum(["file", "library"]),
  documentId: z.string().nullish(),
  documentName: documentNameSchema,
  page: z.number().nullish(),
  quote: z.string(),
  chunkText: z.string().nullish(),
  libraryId: z.string().nullish(),
  libraryName: z.string().nullish(),
})

const SupportingSourceRefSchema = SourceRefSchema.extend({
  page: z.number().nullish(),
})

const SupportingSourceSnippetSchema = SourceSnippetSchema

const RequirementApiSchema = z.object({
  id: z.string(),
  description: z.string(),
  source: SourceRefSchema,
  confidence: z.enum(["saker", "osaker", "ej_verifierbar"]).catch("osaker"),
  category: z.string(),
  isMandatory: z.boolean(),
  snippet: SourceSnippetSchema.nullish(),
  supportingSources: z.array(SupportingSourceRefSchema).catch([]),
  supportingSnippets: z.array(SupportingSourceSnippetSchema).catch([]),
  reasoning: z.string().nullish(),
})

const EvaluationCriterionApiSchema = z.object({
  id: z.string(),
  name: z.string(),
  weightPercent: z.number(),
  description: z.string(),
  source: SourceRefSchema,
  confidence: z.enum(["saker", "osaker", "ej_verifierbar"]).catch("osaker"),
  snippet: SourceSnippetSchema.nullish(),
  supportingSources: z.array(SupportingSourceRefSchema).catch([]),
  supportingSnippets: z.array(SupportingSourceSnippetSchema).catch([]),
  reasoning: z.string().nullish(),
})

const ContractTermApiSchema = z.object({
  id: z.string(),
  clause: z.string(),
  description: z.string(),
  riskLevel: z.enum(["lag", "medel", "hog"]).catch("medel"),
  flagged: z.boolean(),
  source: SourceRefSchema,
  snippet: SourceSnippetSchema.nullish(),
  supportingSources: z.array(SupportingSourceRefSchema).catch([]),
  supportingSnippets: z.array(SupportingSourceSnippetSchema).catch([]),
  frameworkDeviation: z
    .object({
      standard: z.string(),
      deviationDescription: z.string(),
    })
    .nullish(),
  reasoning: z.string().nullish(),
})

const CriticalDateApiSchema = z.object({
  id: z.string(),
  event: z.string(),
  date: z.string().nullish(),
  dateText: z.string().nullish(),
  datePrecision: z.string().nullish(),
  daysRemaining: z.number().nullish(),
  urgency: z.enum(["kritisk", "varning", "normal"]).catch("normal"),
  rowOrigin: z.enum(["ai", "manual"]).optional(),
  snippet: SourceSnippetSchema.nullish(),
  supportingSources: z.array(SupportingSourceRefSchema).catch([]),
  supportingSnippets: z.array(SupportingSourceSnippetSchema).catch([]),
  reasoning: z.string().nullish(),
})

const CriticalDateUserRowApiSchema = z.object({
  id: z.string(),
  event: z.string(),
  date: z.string().nullish(),
  dateText: z.string().nullish(),
  datePrecision: z.string().nullish(),
  reasoning: z.string().nullish(),
})

const RoleRequirementApiSchema = z.object({
  id: z.string(),
  roleName: z.string(),
  competencyLevel: z.string().nullish(),
  lot: z.string().nullish(),
  requirementStage: z
    .enum(["bid", "call_off", "performance", "unknown"])
    .catch("unknown")
    .default("unknown"),
  quantity: z.number(),
  educationLevel: z.string().nullish(),
  minYearsExperience: z.number().nullish(),
  requiredCertifications: z.array(z.string()),
  requiredExperience: z.array(z.string()),
  meritingQualifications: z.array(z.string()),
  source: SourceRefSchema,
  snippet: SourceSnippetSchema.nullish(),
  supportingSources: z.array(SupportingSourceRefSchema).catch([]),
  supportingSnippets: z.array(SupportingSourceSnippetSchema).catch([]),
  reasoning: z.string().nullish(),
})

const SubmissionStageSchema = z
  .enum(["bid", "on_request", "award", "call_off", "performance", "unknown"])
  .catch("unknown")

const BidDeliverableApiSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string(),
  isRequired: z.boolean(),
  submissionStage: SubmissionStageSchema.default("unknown"),
  condition: z.string().nullish(),
  source: SourceRefSchema,
  snippet: SourceSnippetSchema.nullish(),
  supportingSources: z.array(SupportingSourceRefSchema).catch([]),
  supportingSnippets: z.array(SupportingSourceSnippetSchema).catch([]),
  reasoning: z.string().nullish(),
})

const DeliverableDocumentApiSchema = z.object({
  id: z.string(),
  documentName: z.string(),
  description: z.string(),
  mustInclude: z.array(z.string()).catch([]),
  isRequired: z.boolean(),
  submissionStage: SubmissionStageSchema.default("unknown"),
  condition: z.string().nullish(),
  sourceDeliverableIds: z.array(z.string()).catch([]),
  source: SourceRefSchema,
  snippet: SourceSnippetSchema.nullish(),
  supportingSources: z.array(SupportingSourceRefSchema).catch([]),
  supportingSnippets: z.array(SupportingSourceSnippetSchema).catch([]),
  reasoning: z.string().nullish(),
})

const TimelineEntryApiSchema = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string(),
  date: z.string().nullish(),
  dateText: z.string().nullish(),
  datePrecision: z.string().nullish(),
  phase: z
    .enum([
      "pre_submission",
      "submission",
      "evaluation",
      "contract_start",
      "contract",
      "post_contract",
    ])
    .catch("pre_submission"),
  urgency: z.enum(["kritisk", "varning", "normal"]).catch("normal"),
  milestoneKind: z.string().nullish(),
  sourceDateIds: z.array(z.string()).catch([]),
  source: SourceRefSchema,
  snippet: SourceSnippetSchema.nullish(),
  supportingSources: z.array(SupportingSourceRefSchema).catch([]),
  supportingSnippets: z.array(SupportingSourceSnippetSchema).catch([]),
  reasoning: z.string().nullish(),
})

const ProcurementOverviewApiSchema = z.object({
  description: z.string(),
  highlights: z.array(z.string()),
  procurementType: z.string().nullish(),
  domain: z.string().nullish(),
  client: z.string().nullish(),
  intermediary: z.string().nullish(),
  frameworkRef: z.string().nullish(),
  contractStandard: z.string().nullish(),
  estimatedValue: z.string().nullish(),
  scopeSummary: z.string().nullish(),
  positionsCount: z.number().nullish(),
  workModel: z.string().nullish(),
  optionDescription: z.string().nullish(),
  contractDuration: z.string().nullish(),
  submissionDeadline: z.string().nullish(),
})

const SubmissionDeadlineOverrideSchema = z.object({
  value: z.string(),
  reason: z.string().nullish(),
  changedAt: z.string().nullish(),
  changedByUserId: z.string().nullish(),
  changedByDisplayName: z.string().nullish(),
})

const SubmissionDeadlineHistoryEntrySchema = z.object({
  id: z.string(),
  action: z.enum(["set", "update", "clear"]),
  value: z.string().nullish(),
  previousValue: z.string().nullish(),
  reason: z.string().nullish(),
  changedAt: z.string().nullish(),
  changedByUserId: z.string().nullish(),
  changedByDisplayName: z.string().nullish(),
})

const SubmissionDeadlineStateSchema = z.object({
  effectiveValue: z.string().nullish(),
  extractedValue: z.string().nullish(),
  source: z.enum(["auto", "manual", "missing"]),
  isManual: z.boolean(),
  requiresManualEntry: z.boolean(),
  override: SubmissionDeadlineOverrideSchema.nullish(),
  history: z.array(SubmissionDeadlineHistoryEntrySchema).catch([]),
})

const PipelineFailureReasonSchema = z.enum([
  "pipeline_error",
  "user_cancelled",
  "worker_lost",
  "hard_timeout",
  "rate_limited_exhausted",
])

const RateLimitRetryStateSchema = z
  .object({
    attempt: z.number(),
    nextRetryAt: z.string(),
    phase: z.string(),
    sleepSeconds: z.number(),
  })
  .passthrough()
  .nullish()

const PipelineAbortResponseSchema = z.object({
  status: z.string(),
  abortRequested: z.boolean(),
  cancelRequestedAt: z.string().nullish(),
  localCancelTriggered: z.boolean().nullish(),
})

/** Round-2 structured infobox: four punchy strings, no prose. Replaces the
 * old quick_overview markdown blob. */
const QuickOverviewObjectSchema = z.object({
  upphandling: z.string().catch(""),
  avtalstid: z.string().catch(""),
  volym: z.string().catch(""),
  personnel: z.string().catch(""),
})

/** Matches backend `PmContentSchema` (focused PM export, round 2). */
const PmContentObjectSchema = z.object({
  quick_overview: QuickOverviewObjectSchema,
  evaluation_interpretation: z.string(),
  other_terms_summary: z.string(),
})

/** Adapt old Cosmos payloads to the round-2 shape:
 * - prose `quick_overview: string` → structured object (everything funneled to upphandling)
 * - drop `executive_summary` and `action_items` (no longer rendered)
 * - drop legacy `overview_assessment` / `go_nogo`
 */
function normalizePmContentInput(val: unknown): unknown {
  if (val === null || val === undefined) return val
  if (typeof val !== "object") return val
  const o = val as Record<string, unknown>

  let quickOverview: Record<string, string>
  const rawOverview = o.quick_overview
  if (rawOverview && typeof rawOverview === "object" && !Array.isArray(rawOverview)) {
    const obj = rawOverview as Record<string, unknown>
    quickOverview = {
      upphandling: typeof obj.upphandling === "string" ? obj.upphandling : "",
      avtalstid: typeof obj.avtalstid === "string" ? obj.avtalstid : "",
      volym: typeof obj.volym === "string" ? obj.volym : "",
      personnel: typeof obj.personnel === "string" ? obj.personnel : "",
    }
  } else if (typeof rawOverview === "string") {
    // Legacy prose blob — funnel into upphandling so the user still sees something.
    quickOverview = { upphandling: rawOverview, avtalstid: "", volym: "", personnel: "" }
  } else {
    quickOverview = { upphandling: "", avtalstid: "", volym: "", personnel: "" }
  }

  return {
    quick_overview: quickOverview,
    evaluation_interpretation:
      typeof o.evaluation_interpretation === "string" ? o.evaluation_interpretation : "",
    other_terms_summary: typeof o.other_terms_summary === "string" ? o.other_terms_summary : "",
  }
}

const PmContentApiSchema = z.preprocess(
  (val) => {
    if (val === null || val === undefined) return val
    return normalizePmContentInput(val)
  },
  z.union([z.null(), z.undefined(), PmContentObjectSchema])
)

export const TenderExtractionSchema = z.object({
  id: z.string(),
  caseId: z.string(),
  runId: z.string(),
  status: z.enum(["pending", "processing", "completed", "partial", "failed"]),
  categoryStatus: z.record(z.string(), ExtractionCategoryStatusSchema).catch({}),
  procurementOverview: ProcurementOverviewApiSchema.nullish(),
  documentManifest: z.array(z.unknown()).catch([]),
  requirements: safeArrayOf(RequirementApiSchema, "requirements"),
  evaluationCriteria: safeArrayOf(EvaluationCriterionApiSchema, "evaluationCriteria"),
  contractTerms: safeArrayOf(ContractTermApiSchema, "contractTerms"),
  criticalDates: safeArrayOf(CriticalDateApiSchema, "criticalDates"),
  roleRequirements: safeArrayOf(RoleRequirementApiSchema, "roleRequirements"),
  bidDeliverables: safeArrayOf(BidDeliverableApiSchema, "bidDeliverables"),
  deliverableDocuments: safeArrayOf(DeliverableDocumentApiSchema, "deliverableDocuments"),
  timeline: safeArrayOf(TimelineEntryApiSchema, "timeline"),
  bidSummary: z.object({ summary: z.string() }).passthrough().nullish(),
  pmContent: PmContentApiSchema.nullish(),
  startedAt: z.string().nullish(),
  completedAt: z.string().nullish(),
  error: z.string().nullish(),
  failureReason: PipelineFailureReasonSchema.nullish(),
  cancelRequested: z.boolean().nullish(),
  categoryErrors: z.record(z.string(), z.string()).catch({}),
  // Counts per-category retries the user has explicitly initiated; the
  // failure banner consults this to decide whether to surface a Bug Report
  // path (count >= 1) on top of the always-visible Regenerate button.
  categoryRetryCount: z.record(z.string(), z.number()).catch({}),
  submissionDeadline: SubmissionDeadlineStateSchema,
  criticalDatesRemovedIds: z.array(z.string()).catch([]),
  criticalDatesUserRows: z.array(CriticalDateUserRowApiSchema).catch([]),
  extractedCriticalDateIds: z.array(z.string()).catch([]),
})

export type ExtractionCategoryStatus = z.infer<typeof ExtractionCategoryStatusSchema>
export type TenderExtractionApi = z.infer<typeof TenderExtractionSchema>
export type PmContent = z.infer<typeof PmContentObjectSchema>
export type PmQuickOverview = z.infer<typeof QuickOverviewObjectSchema>
export type DeliverableDocumentApi = z.infer<typeof DeliverableDocumentApiSchema>
export type TimelineEntryApi = z.infer<typeof TimelineEntryApiSchema>
export type PipelineAbortResponse = z.infer<typeof PipelineAbortResponseSchema>
export type SubmissionDeadlineStateApi = z.infer<typeof SubmissionDeadlineStateSchema>

// ── API functions ─────────────────────────────────────────────────────────────

export async function listTenderCases(): Promise<TenderCaseListItemApi[]> {
  const data = await api.get<unknown>(`${BASE}/cases`)
  return parseTenderCaseList(data)
}

/**
 * Parse the landing-page list one case at a time: a case that cannot be read is
 * dropped with a warning instead of hiding every other case. A response that is
 * not a list at all still throws, so the page can say the list failed to load.
 */
function parseTenderCaseList(data: unknown): TenderCaseListItemApi[] {
  const items = z.array(z.unknown()).parse(data)
  const cases: TenderCaseListItemApi[] = []
  for (const item of items) {
    const result = TenderCaseListItemSchema.safeParse(item)
    if (result.success) {
      cases.push(result.data)
    } else {
      const id = (item as { id?: unknown } | null)?.id
      logger.warn(`[tender] Dropped unreadable case ${JSON.stringify(id ?? null)} from the list`, {
        issues: result.error.issues,
      })
    }
  }
  // Every case unreadable means the response shape changed, not that the tenant has no
  // tenders: fail the query so the landing page shows its load error instead of "no tenders".
  if (items.length > 0 && cases.length === 0) {
    throw new Error(`None of the ${items.length} tender cases in the response could be read`)
  }
  return cases
}

export async function createTenderCase(
  title: string,
  options?: { procuringAuthority?: string | null }
): Promise<TenderCaseApi> {
  const body: { title: string; procuringAuthority?: string | null } = { title }
  if (options !== undefined && "procuringAuthority" in options) {
    body.procuringAuthority = options.procuringAuthority ?? null
  }
  const data = await api.post<TenderCaseApi>(`${BASE}/cases`, body)
  return TenderCaseSchema.parse(data)
}

export type QuickStartResult = { caseId: string; runId: string }

// ── SAS direct-to-blob upload helpers (currently UNUSED) ──────────────────────
//
// These power a direct-to-blob upload flow (frontend PUTs files straight to
// Azure Blob via short-lived SAS URLs minted by /api/tender/uploads/sas) that
// avoided round-tripping bytes through the API. We swapped back to the
// per-file POST flow below because the SAS path's operational cost (storage
// CORS, network ACL drift, RBAC dependencies) outweighed its benefits at
// current upload volumes. The helpers + the backend /uploads/sas endpoint and
// the JSON-body branch on /quick-start all stay in place as dead code so the
// SAS path can be re-enabled without rewiring if upload sizes grow.

type IssuedSasFile = {
  filename: string
  docId: string
  blobPath: string
  sasUrl: string
  contentType: string
}

type IssueSasResponse = { caseId: string; reservationGeneration: string; files: IssuedSasFile[] }

/** UNUSED: ask the backend to mint short-lived SAS URLs for direct PUT to blob. */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
async function issueTenderUploadSasUrls(
  files: { filename: string; contentType: string }[]
): Promise<IssueSasResponse> {
  return api.post<{ files: { filename: string; contentType: string }[] }, IssueSasResponse>(
    `${BASE}/uploads/sas`,
    { files }
  )
}

/** UNUSED: upload a single file to a SAS URL (XHR for progress events). */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
async function uploadFileToSasUrl(args: {
  file: File
  sasUrl: string
  contentType: string
  onProgress?: (progress: { loaded: number; total: number }) => void
}): Promise<void> {
  const { file, sasUrl, contentType, onProgress } = args
  await new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open("PUT", sasUrl)
    xhr.setRequestHeader("x-ms-blob-type", "BlockBlob")
    xhr.setRequestHeader("Content-Type", contentType)
    if (onProgress) {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) onProgress({ loaded: e.loaded, total: e.total })
      }
    }
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve()
      else reject(new Error(`Blob upload failed (${xhr.status}): ${xhr.responseText}`))
    }
    xhr.onerror = () => reject(new Error("Blob upload network error"))
    xhr.send(file)
  })
}

// ── Active per-file POST upload flow ──────────────────────────────────────────
//
// Frontend creates the case, POSTs each file individually (parallel, capped
// by the browser's connections-per-origin limit at ~6), then asks the backend
// to start the auto-pipeline. Each request carries one file so worker memory
// stays bounded at ~1 file regardless of batch size, and a per-file XHR gives
// us granular upload-progress events.

/**
 * One file upload that failed. `status` is 0 when the request never got a
 * response. The message is for logs; the UI shows a translated text built
 * from `fileName` and `status`.
 */
export class TenderFileUploadError extends Error {
  readonly fileName: string
  readonly status: number

  constructor(fileName: string, status: number, responseText = "") {
    const detail = responseText.trim().slice(0, 200)
    super(
      status === 0
        ? `Upload network error for "${fileName}"`
        : `Upload failed for "${fileName}" (${status})${detail ? `: ${detail}` : ""}`
    )
    this.name = "TenderFileUploadError"
    this.fileName = fileName
    this.status = status
  }
}

const UPLOAD_ATTEMPTS = 3
const UPLOAD_RETRY_BASE_DELAY_MS = 1000

/**
 * Transient failures: no response at all, a timeout, throttling or a server
 * error. At Bjerking about 3.5% of file uploads get a 500/502 from App Service
 * without ever reaching the app, and a batch of 25 files then fails more often
 * than not. Retrying the same bytes is safe: the stream endpoint converges on
 * one document per content hash, so a retry after a lost response reuses the
 * document the first attempt created.
 */
function isRetryableUploadFailure(err: unknown): boolean {
  if (!(err instanceof TenderFileUploadError)) return false
  const { status } = err
  return status === 0 || status === 408 || status === 429 || status >= 500
}

/** Stream-upload one file as a raw request body. XHR for progress events.
 *
 * Raw body (not multipart) so the backend can stream chunks directly to blob
 * via Quart's async request body iterator. Filename and run/doc-type metadata
 * travel in headers/query params since multipart's per-part headers aren't
 * available. Worker memory stays bounded at the chunk size regardless of how
 * large the file is.
 */
async function uploadOneTenderFile(args: {
  caseId: string
  runId: string
  file: File
  docType?: "procurement" | "context" | "bid" | "cv"
  onProgress?: (progress: { loaded: number; total: number }) => void
}): Promise<{ docId: string }> {
  const { caseId, runId, file, docType = "procurement", onProgress } = args

  // Encode the filename so non-ASCII (Swedish chars) survives the header
  // round-trip; backend urldecodes.
  const encodedFilename = encodeURIComponent(file.name)
  const contentType = file.type || "application/octet-stream"
  // BACKEND_URI already ends in /api — don't prefix it again.
  const url =
    `${BACKEND_URI}${BASE}/cases/${caseId}/documents-stream` +
    `?run_id=${encodeURIComponent(runId)}&doc_type=${docType}`

  return new Promise<{ docId: string }>((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open("POST", url)
    xhr.setRequestHeader("Content-Type", contentType)
    xhr.setRequestHeader("X-Filename", encodedFilename)
    const token = SettingsService.getInstance().token
    if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`)
    xhr.setRequestHeader(TENDER_ADMIN_MODE_HEADER, tenderAdminModeHeaderValue())
    if (onProgress) {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) onProgress({ loaded: e.loaded, total: e.total })
      }
    }
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const body = JSON.parse(xhr.responseText) as { docId?: string }
          if (!body.docId) {
            reject(new Error("Upload succeeded but server returned no doc id"))
            return
          }
          resolve({ docId: body.docId })
        } catch (parseErr) {
          reject(
            parseErr instanceof Error ? parseErr : new Error("Could not parse upload response")
          )
        }
      } else {
        reject(new TenderFileUploadError(file.name, xhr.status, xhr.responseText))
      }
    }
    xhr.onerror = () => reject(new TenderFileUploadError(file.name, 0))
    // Send the File object directly as the body — browser streams from disk.
    xhr.send(file)
  })
}

async function uploadOneTenderFileWithRetry(
  args: Parameters<typeof uploadOneTenderFile>[0]
): Promise<{ docId: string }> {
  for (let attempt = 1; ; attempt += 1) {
    try {
      return await uploadOneTenderFile(args)
    } catch (err) {
      if (attempt >= UPLOAD_ATTEMPTS || !isRetryableUploadFailure(err)) throw err
      // The retry re-sends the whole file, so its progress starts over.
      args.onProgress?.({ loaded: 0, total: args.file.size })
      // Jittered backoff (~1 s, then ~2 s) so parallel uploads that failed
      // together don't retry in lockstep.
      const delayMs = UPLOAD_RETRY_BASE_DELAY_MS * 2 ** (attempt - 1) * (0.5 + Math.random())
      await new Promise((resolve) => setTimeout(resolve, delayMs))
    }
  }
}

/**
 * The pipeline-start POST failed after the case and its documents were
 * created successfully. Carries everything needed to retry the start against
 * the SAME case — the caller must never treat this as license to delete the
 * case: a lost response is indistinguishable from an accepted request, so the
 * pipeline may already be running, and document workers spawned by the
 * uploads may still be processing.
 */
export class QuickStartStartError extends Error {
  readonly caseId: string
  readonly runId: string
  readonly cvLibraryId?: string
  readonly overrideDocIds: string[]

  constructor(args: {
    caseId: string
    runId: string
    cvLibraryId?: string
    overrideDocIds: string[]
    cause: unknown
  }) {
    super(
      args.cause instanceof Error
        ? args.cause.message
        : `Quick-start pipeline start failed: ${String(args.cause)}`
    )
    this.name = "QuickStartStartError"
    this.caseId = args.caseId
    this.runId = args.runId
    this.cvLibraryId = args.cvLibraryId
    this.overrideDocIds = args.overrideDocIds
  }
}

/**
 * Start (or retry) the auto-pipeline for an already-created case + run.
 *
 * A 409 means another attempt already owns the run — which, on a retry after
 * a lost response, is exactly the "your start actually succeeded" case. The
 * backend admission is an atomic claim, so converging on the persisted winner
 * is correct: treat 409 as success rather than surfacing a false failure.
 */
export async function startTenderAutoPipeline(args: {
  caseId: string
  runId: string
  cvLibraryId?: string
  overrideDocIds: string[]
}): Promise<QuickStartResult> {
  try {
    return await api.post<{ cvLibraryId?: string; overrideDocIds: string[] }, QuickStartResult>(
      `${BASE}/cases/${args.caseId}/runs/${args.runId}/auto-pipeline/start`,
      {
        cvLibraryId: args.cvLibraryId,
        overrideDocIds: args.overrideDocIds,
      }
    )
  } catch (err) {
    const status = (err as { response?: { status?: number } } | null)?.response?.status
    if (status === 409) {
      return { caseId: args.caseId, runId: args.runId }
    }
    throw err
  }
}

/** Per-file quick-start: create case → upload files in parallel → start pipeline. */
export async function quickStartTenderCase(params: {
  title?: string
  cvLibraryId?: string
  procuringAuthority?: string | null
  sourceDocumentsUrl?: string | null
  submissionDeadline?: string | null
  /** Optional "Sista dag för frågor" (YYYY-MM-DD). */
  questionsDeadline?: string | null
  responsible?: TenderPersonRef | null
  participants?: TenderParticipantRef[]
  files: File[]
  overrideFileIndexes?: number[]
  onUploadProgress?: (progress: { loaded: number; total: number }) => void
}): Promise<QuickStartResult> {
  if (params.files.length === 0) {
    throw new Error("At least one procurement document is required")
  }

  // Step 1 — create the case + first run.
  const created = await api.post<
    {
      title?: string
      procuringAuthority?: string | null
      sourceDocumentsUrl?: string | null
      submissionDeadline?: string | null
      questionsDeadline?: string | null
      responsible?: TenderPersonRef | null
      participants?: TenderParticipantRef[]
    },
    TenderCaseApi
  >(`${BASE}/cases`, {
    title: params.title,
    procuringAuthority: params.procuringAuthority ?? undefined,
    sourceDocumentsUrl: params.sourceDocumentsUrl ?? undefined,
    submissionDeadline: params.submissionDeadline ?? undefined,
    questionsDeadline: params.questionsDeadline ?? undefined,
    responsible: params.responsible ?? undefined,
    participants: params.participants?.length ? params.participants : undefined,
  })
  const caseId = created.id
  const runId = created.runs[0]?.id
  if (!runId) throw new Error("Case was created without a first run — cannot continue")

  // Step 2 — upload files concurrently. Browsers cap connections per origin
  // (~6), so this won't actually fan out unbounded. Track per-file progress
  // and aggregate into the same {loaded, total} contract the modal expects.
  const totalBytes = params.files.reduce((sum, f) => sum + f.size, 0)
  const perFileLoaded = new Array<number>(params.files.length).fill(0)
  const reportProgress = () => {
    if (!params.onUploadProgress) return
    const loaded = perFileLoaded.reduce((a, b) => a + b, 0)
    params.onUploadProgress({ loaded, total: totalBytes })
  }

  // Use allSettled so a single fast-failing upload doesn't leave the slower
  // ones hanging in the network tab; we still want every in-flight upload to
  // finish (or fail) before we decide what to do. A transient failure is
  // retried per file first; only a file that still fails makes us clean up
  // the case + already-uploaded blobs by deleting the case (the DELETE route
  // wipes blobs as well), so the user isn't left with an orphaned half-case.
  const settled = await Promise.allSettled(
    params.files.map((file, i) =>
      uploadOneTenderFileWithRetry({
        caseId,
        runId,
        file,
        onProgress: (p) => {
          perFileLoaded[i] = p.loaded
          reportProgress()
        },
      }).then((res) => ({ index: i, docId: res.docId }))
    )
  )

  const failures = settled.filter((r): r is PromiseRejectedResult => r.status === "rejected")
  if (failures.length > 0) {
    try {
      await deleteTenderCase(caseId)
    } catch (cleanupErr) {
      // Don't shadow the upload error — log so the orphan is still findable.
      console.warn("Quick-start cleanup failed for case", caseId, cleanupErr)
    }
    const firstReason = failures[0].reason
    throw firstReason instanceof Error
      ? firstReason
      : new Error(`Quick-start upload failed: ${String(firstReason)}`)
  }

  const uploaded = settled
    .filter(
      (r): r is PromiseFulfilledResult<{ index: number; docId: string }> => r.status === "fulfilled"
    )
    .map((r) => r.value)

  // Step 3 — start the auto-pipeline. Resolve override indexes against the
  // doc ids the backend handed back in step 2, since indexes don't survive
  // multiple separate uploads.
  const overrideDocIds = (params.overrideFileIndexes ?? [])
    .map((idx) => uploaded.find((u) => u.index === idx)?.docId)
    .filter((d): d is string => Boolean(d))

  try {
    return await startTenderAutoPipeline({
      caseId,
      runId,
      cvLibraryId: params.cvLibraryId,
      overrideDocIds,
    })
  } catch (startErr) {
    // Deliberately NO cleanup here. A failed start response is ambiguous — a
    // transport/auth-layer loss can drop the response of an accepted request,
    // in which case the pipeline is already running and deleting the case
    // would destroy live work; even a definite pre-start rejection can race
    // the document workers spawned by the uploads. The backend marks the run
    // failed on definite resolution failures, the case list is invalidated on
    // settle so the case stays visible, and the thrown error carries what the
    // caller needs to retry the start against the same case.
    throw new QuickStartStartError({
      caseId,
      runId,
      cvLibraryId: params.cvLibraryId,
      overrideDocIds,
      cause: startErr,
    })
  }
}

export async function getTenderCase(caseId: string): Promise<TenderCaseApi> {
  const data = await api.get<TenderCaseApi>(`${BASE}/cases/${caseId}`)
  return TenderCaseSchema.parse(data)
}

export async function updateTenderCase(
  caseId: string,
  patch: {
    title?: string
    isTitleAutoGenerated?: boolean
    procuringAuthority?: string | null
    sourceDocumentsUrl?: string | null
    /** "Sista dag för frågor" (YYYY-MM-DD); null clears it. */
    questionsDeadline?: string | null
    /** Explicit null clears the intended Ansvarig; omit the key to leave it alone. */
    responsible?: TenderPersonRef | null
  }
): Promise<TenderCaseApi> {
  const data = await api.patch<TenderCaseApi>(`${BASE}/cases/${caseId}`, patch)
  return TenderCaseSchema.parse(data)
}

export async function createTenderRun(caseId: string, sourceRunId: string): Promise<TenderCaseApi> {
  const data = await api.post<TenderCaseApi>(`${BASE}/cases/${caseId}/runs`, { sourceRunId })
  return TenderCaseSchema.parse(data)
}

export async function deleteTenderCase(caseId: string): Promise<void> {
  await api.delete(`${BASE}/cases/${caseId}`)
}

export async function deleteTenderDocument(
  caseId: string,
  docId: string,
  runId: string
): Promise<TenderCaseApi> {
  const data = await api.delete<TenderCaseApi>(
    `${BASE}/cases/${caseId}/documents/${docId}?runId=${runId}`
  )
  return TenderCaseSchema.parse(data)
}

type UploadTenderDocumentsOptions = {
  runId: string
  docType: "procurement" | "context"
  files: File[]
}

export async function uploadTenderDocuments(
  caseId: string,
  { runId, docType, files }: UploadTenderDocumentsOptions
): Promise<TenderCaseApi> {
  const formData = new FormData()
  formData.append("run_id", runId)
  formData.append("doc_type", docType)
  for (const file of files) {
    formData.append("files", file)
  }
  const data = await api.post<TenderCaseApi>(`${BASE}/cases/${caseId}/documents`, formData)
  return TenderCaseSchema.parse(data)
}

export async function setRunOverrideDocuments(
  caseId: string,
  runId: string,
  overrideDocumentIds: string[]
): Promise<TenderCaseApi> {
  const data = await api.patch<TenderCaseApi>(
    `${BASE}/cases/${caseId}/runs/${runId}/override-documents`,
    { overrideDocumentIds }
  )
  return TenderCaseSchema.parse(data)
}

export async function startExtraction(
  caseId: string,
  runId: string,
  modelId?: string
): Promise<{ extractionId: string; status: string }> {
  const body = modelId ? { model_override: modelId } : {}
  const response = await api.post<{ extractionId: string; status: string }>(
    `/tender/cases/${caseId}/runs/${runId}/extract`,
    body
  )
  return response as unknown as { extractionId: string; status: string }
}

export async function getExtraction(caseId: string, runId: string): Promise<TenderExtractionApi> {
  const response = await api.get(`/tender/cases/${caseId}/runs/${runId}/extraction`)
  return TenderExtractionSchema.parse(response)
}

export async function updateExtractionSubmissionDeadline({
  caseId,
  runId,
  submissionDeadline,
  reason,
}: {
  caseId: string
  runId: string
  submissionDeadline: string | null
  reason?: string
}): Promise<TenderExtractionApi> {
  const response = await api.patch(
    `/tender/cases/${caseId}/runs/${runId}/extraction/submission-deadline`,
    {
      submissionDeadline,
      reason,
    }
  )
  return TenderExtractionSchema.parse(response)
}

export type CriticalDateUserRowApi = z.infer<typeof CriticalDateUserRowApiSchema>

export async function updateExtractionCriticalDates({
  caseId,
  runId,
  removedIds,
  userRows,
}: {
  caseId: string
  runId: string
  removedIds: string[]
  userRows: CriticalDateUserRowApi[]
}): Promise<TenderExtractionApi> {
  const response = await api.patch(
    `/tender/cases/${caseId}/runs/${runId}/extraction/critical-dates`,
    {
      removedIds,
      userRows,
    }
  )
  return TenderExtractionSchema.parse(response)
}

export async function abortExtraction(
  caseId: string,
  runId: string
): Promise<PipelineAbortResponse> {
  const response = await api.post(`/tender/cases/${caseId}/runs/${runId}/extraction/abort`)
  return PipelineAbortResponseSchema.parse(response)
}

export async function retryExtractionCategories(
  caseId: string,
  runId: string,
  categories: string[]
): Promise<{ extractionId: string; status: string; categories: string[] }> {
  const response = await api.post<{ extractionId: string; status: string; categories: string[] }>(
    `/tender/cases/${caseId}/runs/${runId}/extract/retry`,
    { categories }
  )
  return response as unknown as { extractionId: string; status: string; categories: string[] }
}

type GetTenderDocumentUrlParams = {
  caseId: string
  docId: string
  filename: string
  page?: number
}

export function getTenderDocumentUrl({
  caseId,
  docId,
  filename,
  page,
}: GetTenderDocumentUrlParams): string {
  const base = `${BACKEND_URI}/tender/cases/${caseId}/documents/${docId}/content/${encodeURIComponent(filename)}`
  // 0 or null means the page is unknown (see features/tender/utils/source-page).
  return typeof page === "number" && page > 0 ? `${base}#page=${page}` : base
}

function safeFilename(title: string): string {
  return title
    .slice(0, 50)
    .replace(/\s+/g, "-")
    .replace(/[^\w\-åäöÅÄÖ]/g, "")
}

export async function downloadExtractionDocx(
  caseId: string,
  runId: string,
  caseTitle: string
): Promise<void> {
  const token = SettingsService.getInstance().token
  const response = await fetchWithAuthRetry(
    `${BACKEND_URI}/tender/cases/${caseId}/runs/${runId}/extraction/export`,
    { method: "GET", headers: getTenderHeaders(token) },
    token
  )
  const blob = await response.blob()
  const { saveAs } = await import("file-saver")
  saveAs(blob, `extrahering-${safeFilename(caseTitle)}.docx`)
}

export async function downloadPmDocx(
  caseId: string,
  runId: string,
  caseTitle: string
): Promise<void> {
  const token = SettingsService.getInstance().token
  const response = await fetchWithAuthRetry(
    `${BACKEND_URI}/tender/cases/${caseId}/runs/${runId}/extraction/export-pm`,
    { method: "GET", headers: getTenderHeaders(token) },
    token
  )
  const blob = await response.blob()
  const { saveAs } = await import("file-saver")
  // Prefix with "HelloAI-Export-PM-" (not just "pm-") so that when the user
  // hands the file over alongside the procurer's tender documents it's
  // obvious at a glance that this is an AI-generated export and not part
  // of the underlying tender package. Feedback from bid managers: the
  // old "pm-<title>" name was easy to mistake for procurer-supplied
  // material.
  saveAs(blob, `HelloAI-Export-PM-${safeFilename(caseTitle)}.docx`)
}

export async function downloadBidReviewDocx(
  caseId: string,
  runId: string,
  caseTitle: string
): Promise<void> {
  const token = SettingsService.getInstance().token
  const response = await fetchWithAuthRetry(
    `${BACKEND_URI}/tender/cases/${caseId}/runs/${runId}/bid-review/export`,
    { method: "GET", headers: getTenderHeaders(token) },
    token
  )
  const blob = await response.blob()
  const { saveAs } = await import("file-saver")
  saveAs(blob, `anbudsgranskning-${safeFilename(caseTitle)}.docx`)
}

export async function downloadCvMatchDocx(
  caseId: string,
  runId: string,
  caseTitle: string
): Promise<void> {
  const token = SettingsService.getInstance().token
  const response = await fetchWithAuthRetry(
    `${BACKEND_URI}/tender/cases/${caseId}/runs/${runId}/cv-match/export`,
    { method: "GET", headers: getTenderHeaders(token) },
    token
  )
  const blob = await response.blob()
  const { saveAs } = await import("file-saver")
  saveAs(blob, `cv-matchning-${safeFilename(caseTitle)}.docx`)
}

export async function downloadSetupFilesZip(
  caseId: string,
  runId: string,
  caseTitle: string
): Promise<void> {
  const token = SettingsService.getInstance().token
  const response = await fetchWithAuthRetry(
    `${BACKEND_URI}/tender/cases/${caseId}/runs/${runId}/setup-files/zip`,
    { method: "GET", headers: getTenderHeaders(token) },
    token
  )
  const blob = await response.blob()
  const { saveAs } = await import("file-saver")
  saveAs(blob, `HelloAI-Setup-${safeFilename(caseTitle)}.zip`)
}

export async function downloadTenderDocument(
  caseId: string,
  docId: string,
  filename: string
): Promise<void> {
  const token = SettingsService.getInstance().token
  const response = await fetchWithAuthRetry(
    `${BACKEND_URI}/tender/cases/${caseId}/documents/${docId}/content/${encodeURIComponent(filename)}`,
    { method: "GET", headers: getTenderHeaders(token) },
    token
  )
  const blob = await response.blob()
  const { saveAs } = await import("file-saver")
  saveAs(blob, filename)
}

// ── Chat session API functions ────────────────────────────────────────────────

export async function createTenderChatSession(
  caseId: string,
  runId?: string
): Promise<ChatSessionModel> {
  return api.post<never, ChatSessionModel>(
    `${BASE}/cases/${caseId}/chat-sessions`,
    runId ? { runId } : undefined
  )
}

export async function listTenderChatSessionIds(caseId: string): Promise<ChatSessionModel[]> {
  return api.get<never, ChatSessionModel[]>(`${BASE}/cases/${caseId}/chat-sessions`)
}

export async function deleteTenderChatSession(caseId: string, sessionId: string): Promise<void> {
  await api.delete(`${BASE}/cases/${caseId}/chat-sessions/${sessionId}`)
}

// ── Bid review schemas ────────────────────────────────────────────────────────

const BidReviewFindingSchema = z.object({
  id: z.string(),
  findingType: z.enum([
    "missing_requirement",
    "incomplete_requirement",
    "missing_deliverable",
    "missing_role",
    "risk",
    "suggestion",
    "strength",
  ]),
  title: z.string(),
  description: z.string(),
  severity: z.enum(["critical", "major", "minor", "info"]),
  relatedRequirementId: z.string().nullish(),
  relatedDeliverableId: z.string().nullish(),
  suggestedAction: z.string().nullish(),
})

const RequirementStatusSchema = z.object({
  requirementId: z.string(),
  requirementDescription: z.string(),
  status: z.enum(["met", "partial", "missing"]),
  note: z.string().nullish(),
})

export const BidReviewSchema = z.object({
  id: z.string(),
  caseId: z.string(),
  runId: z.string(),
  status: z.enum(["pending", "processing", "completed", "failed"]),
  bidDocumentFilenames: z.array(z.string()),
  overallAssessment: z.string().nullish(),
  complianceScore: z.number().nullish(),
  requirementStatuses: z.array(RequirementStatusSchema).default([]),
  findings: z.array(BidReviewFindingSchema).default([]),
  startedAt: z.string().nullish(),
  completedAt: z.string().nullish(),
  error: z.string().nullish(),
  failureReason: PipelineFailureReasonSchema.nullish(),
  cancelRequested: z.boolean().nullish(),
  // Multi-pass progressive fields
  completedPasses: z.array(z.string()).nullish(),
  passErrors: z.record(z.string(), z.string()).nullish(),
  selectedFrameworks: z.array(z.string()).nullish(),
  requirementAnalyses: z.array(z.unknown()).nullish(),
  roleAnalyses: z.array(z.unknown()).nullish(),
  deliverableAnalyses: z.array(z.unknown()).nullish(),
  criterionScores: z.array(z.unknown()).nullish(),
  totalPredictedScore: z.number().nullish(),
  totalMaxScore: z.number().nullish(),
  scoringNotes: z.string().nullish(),
  frameworkAnalyses: z.array(z.unknown()).nullish(),
  contractAnalyses: z.array(z.unknown()).nullish(),
  timelineChecks: z.array(z.unknown()).nullish(),
  priorityItems: z.array(z.unknown()).nullish(),
  improvementOpportunities: z.array(z.unknown()).nullish(),
  strengthsSummary: z.array(z.string()).nullish(),
  competitiveNotes: z.string().nullish(),
  goNoGo: z.enum(["go", "cautious_go", "no_go"]).nullish(),
  goNoGoReasoning: z.string().nullish(),
  staffingPlanIncluded: z.boolean().nullish(),
  staffingAnalysis: z.unknown().nullish(),
  rateLimitRetry: RateLimitRetryStateSchema,
})

export type BidReviewApi = z.infer<typeof BidReviewSchema>

// ── Bid review API functions ──────────────────────────────────────────────────

export async function uploadBidDocument(
  caseId: string,
  { runId, files }: { runId: string; files: File[] }
): Promise<TenderCaseApi> {
  const formData = new FormData()
  formData.append("run_id", runId)
  formData.append("doc_type", "bid")
  files.forEach((file) => {
    formData.append("files", file)
  })
  const data = await api.post<TenderCaseApi>(`${BASE}/cases/${caseId}/documents`, formData)
  return TenderCaseSchema.parse(data)
}

export async function startBidReview(
  caseId: string,
  runId: string,
  options?: { frameworks?: string[]; modelId?: string }
): Promise<{ bidReviewId: string; status: string }> {
  const { frameworks, modelId } = options ?? {}
  const body: Record<string, unknown> = {}
  if (frameworks !== undefined) body.frameworks = frameworks
  if (modelId) body.model_override = modelId
  const response = await api.post<{ bidReviewId: string; status: string }>(
    `/tender/cases/${caseId}/runs/${runId}/bid-review`,
    body
  )
  return response as unknown as { bidReviewId: string; status: string }
}

export async function getBidReview(caseId: string, runId: string): Promise<BidReviewApi> {
  const response = await api.get(`/tender/cases/${caseId}/runs/${runId}/bid-review`)
  return BidReviewSchema.parse(response)
}

export async function abortBidReview(
  caseId: string,
  runId: string
): Promise<PipelineAbortResponse> {
  const response = await api.post(`/tender/cases/${caseId}/runs/${runId}/bid-review/abort`)
  return PipelineAbortResponseSchema.parse(response)
}

export async function retryBidReviewPasses(
  caseId: string,
  runId: string,
  passes: string[]
): Promise<{ bidReviewId: string; status: string; passes: string[] }> {
  const response = await api.post<{ bidReviewId: string; status: string; passes: string[] }>(
    `/tender/cases/${caseId}/runs/${runId}/bid-review/retry`,
    { passes }
  )
  return response as unknown as { bidReviewId: string; status: string; passes: string[] }
}

// ── LLM trace schemas ─────────────────────────────────────────────────────────

export const LlmCallTraceSchema = z.object({
  phase: z.string(),
  model: z.string(),
  temperature: z.number(),
  systemPrompt: z.string(),
  userMessage: z.string(),
  userMessageTruncated: z.boolean().default(false),
  responseContent: z.string(),
  promptTokens: z.number(),
  completionTokens: z.number(),
  totalTokens: z.number(),
  cachedTokens: z.number().default(0),
  durationMs: z.number(),
  timestamp: z.string(),
})

export const LlmPhaseMetricSchema = z
  .object({
    phase: z.string(),
    durationMs: z.number().nonnegative().optional(),
    completedAt: z.string().optional(),
    outcome: z.string().optional(),
    pipelineVersion: z.string().optional(),
    failureReason: z.string().nullish(),
    error: z.string().optional(),
  })
  .passthrough()

export const LlmTraceSchema = z.object({
  id: z.string(),
  caseId: z.string(),
  runId: z.string(),
  step: z.string(),
  calls: z.array(LlmCallTraceSchema),
  callCount: z.number().int().nonnegative().optional(),
  phaseMetrics: z.array(LlmPhaseMetricSchema).default([]),
  pipelineVersion: z.string().optional(),
  attemptId: z.string().nullish(),
  attemptKind: z.string().nullish(),
  failureReason: z.string().nullish(),
  rawTraceExpiredAt: z.string().nullish(),
})

export type LlmCallTrace = z.infer<typeof LlmCallTraceSchema>
export type LlmPhaseMetric = z.infer<typeof LlmPhaseMetricSchema>
export type LlmTrace = z.infer<typeof LlmTraceSchema>

// ── LLM trace API functions ───────────────────────────────────────────────────

export async function fetchLlmTrace(
  caseId: string,
  runId: string,
  step: "extraction" | "bid-review"
): Promise<LlmTrace> {
  const response = await api.get(`/tender/cases/${caseId}/runs/${runId}/llm-trace/${step}`)
  return LlmTraceSchema.parse(response)
}

export async function downloadLlmTraceDocx(
  caseId: string,
  runId: string,
  options: { step: "extraction" | "bid-review"; caseTitle: string }
): Promise<void> {
  const { step, caseTitle } = options
  const token = SettingsService.getInstance().token
  const response = await fetchWithAuthRetry(
    `${BACKEND_URI}/tender/cases/${caseId}/runs/${runId}/llm-trace/${step}/export`,
    { method: "GET", headers: getTenderHeaders(token) },
    token
  )
  const blob = await response.blob()
  const { saveAs } = await import("file-saver")
  saveAs(blob, `llm-spar-${step}-${safeFilename(caseTitle)}.docx`)
}

export async function downloadLlmTraceCsv(
  caseId: string,
  runId: string,
  options: { step: "extraction" | "bid-review"; caseTitle: string }
): Promise<void> {
  const { step, caseTitle } = options
  const token = SettingsService.getInstance().token
  const response = await fetchWithAuthRetry(
    `${BACKEND_URI}/tender/cases/${caseId}/runs/${runId}/llm-trace/${step}/export-csv`,
    { method: "GET", headers: getTenderHeaders(token) },
    token
  )
  const blob = await response.blob()
  const { saveAs } = await import("file-saver")
  saveAs(blob, `llm-spar-${step}-${safeFilename(caseTitle)}.csv`)
}

// ── Extraction timing ─────────────────────────────────────────────────────────

export type ExtractionTimingMetric = {
  phase: string
  durationMs: number
  completedAt: string
  outcome: string
  pipelineVersion?: string
  itemCount?: number
  documentCount?: number
}

export type ExtractionTimingResponse = {
  phaseMetrics: ExtractionTimingMetric[]
  pipelineVersion: string
}

export async function fetchExtractionTiming(
  caseId: string,
  runId: string
): Promise<ExtractionTimingResponse> {
  return api.get(`/tender/cases/${caseId}/runs/${runId}/extraction/timing`)
}

// ── CV match schemas ──────────────────────────────────────────────────────────

const RequirementEvidenceSchema = z.object({
  requirementText: z.string(),
  status: z.enum(["matched", "missing", "overqualified"]).default("missing"),
  reasoning: z.string().default(""),
  sourceDocument: z.string().default(""),
  sourceDocId: z.string().default(""),
  sourceQuote: z.string().default(""),
  sourceType: z.enum(["tender", "library"]).default("library"),
})

const CandidateMatchSchema = z.object({
  candidateId: z.string(),
  displayName: z.string(),
  matchedRequirements: z.array(z.string()).default([]),
  missingRequirements: z.array(z.string()).default([]),
  meritingMatches: z.array(z.string()).default([]),
  overqualifiedRequirements: z.array(z.string()).default([]),
  requirementEvidence: z.array(RequirementEvidenceSchema).optional(),
  sourceSnippet: z.string().default(""),
  notes: z.string().default(""),
  cvSource: z.string().optional().default(""),
})

const ClassifiedRequirementSchema = z.object({
  text: z.string(),
  type: z.enum(["hard", "soft"]),
  overqualifiedCounts: z.boolean().default(false),
})

const ClassifiedReqListSchema = z.object({
  hard: z.array(ClassifiedRequirementSchema).default([]),
  soft: z.array(ClassifiedRequirementSchema).default([]),
})

const RoleCandidatePoolSchema = z.object({
  roleId: z.string(),
  roleName: z.string(),
  quantityRequired: z.number(),
  candidates: z.array(CandidateMatchSchema).default([]),
  classifiedRequirements: ClassifiedReqListSchema.optional(),
})

const LockedCandidateSchema = z.object({
  roleId: z.string(),
  candidateId: z.string(),
  displayName: z.string(),
  lockedAt: z.string(),
})

const CvMatchResultSchema = z.object({
  id: z.string().nullish(),
  caseId: z.string().nullish(),
  runId: z.string().nullish(),
  status: z.string().nullish(),
  cvLibraryId: z.string(),
  rolePools: z.array(RoleCandidatePoolSchema).default([]),
  lockedCandidates: z.array(LockedCandidateSchema).default([]),
  completedAt: z.string().nullish(),
  startedAt: z.string().nullish(),
  error: z.string().nullish(),
  failureReason: PipelineFailureReasonSchema.nullish(),
  cancelRequested: z.boolean().nullish(),
  rateLimitRetry: RateLimitRetryStateSchema,
})

export type CvMatchResultApi = z.infer<typeof CvMatchResultSchema>
export type LockedCandidateApi = z.infer<typeof LockedCandidateSchema>

// ── CV match API functions ────────────────────────────────────────────────────

export async function startCvMatch(
  caseId: string,
  runId: string,
  cvLibraryId: string
): Promise<CvMatchResultApi> {
  const response = await api.post(`/tender/cases/${caseId}/runs/${runId}/cv-match`, {
    cv_library_id: cvLibraryId,
  })
  return CvMatchResultSchema.parse(response)
}

export async function getCvMatch(caseId: string, runId: string): Promise<CvMatchResultApi> {
  const response = await api.get(`/tender/cases/${caseId}/runs/${runId}/cv-match`)
  return CvMatchResultSchema.parse(response)
}

export async function abortCvMatch(caseId: string, runId: string): Promise<PipelineAbortResponse> {
  const response = await api.post(`/tender/cases/${caseId}/runs/${runId}/cv-match/abort`)
  return PipelineAbortResponseSchema.parse(response)
}

export async function deleteCvMatch(caseId: string, runId: string): Promise<void> {
  await api.delete(`/tender/cases/${caseId}/runs/${runId}/cv-match`)
}

export async function classifyCvRequirements(
  caseId: string,
  runId: string
): Promise<CvMatchResultApi> {
  const response = await api.post(`/tender/cases/${caseId}/runs/${runId}/cv-classify`)
  return CvMatchResultSchema.parse(response)
}

export async function analyseDocForCvMatch(
  caseId: string,
  runId: string,
  params: { docId: string; cvLibraryId: string; cvSource?: string }
): Promise<CvMatchResultApi> {
  const response = await api.post(`/tender/cases/${caseId}/runs/${runId}/cv-match/analyse-doc`, {
    doc_id: params.docId,
    cv_library_id: params.cvLibraryId,
    ...(params.cvSource ? { cv_source: params.cvSource } : {}),
  })
  return CvMatchResultSchema.parse(response)
}

export async function deleteCvMatchCandidate(
  caseId: string,
  runId: string,
  candidateId: string
): Promise<CvMatchResultApi> {
  const response = await api.delete(
    `/tender/cases/${caseId}/runs/${runId}/cv-match/candidates/${encodeURIComponent(candidateId)}`
  )
  return CvMatchResultSchema.parse(response)
}

export async function updateLockedCandidates(
  caseId: string,
  runId: string,
  lockedCandidates: LockedCandidateApi[]
): Promise<CvMatchResultApi> {
  const response = await api.patch(`/tender/cases/${caseId}/runs/${runId}/cv-match/locked`, {
    locked_candidates: lockedCandidates,
  })
  return CvMatchResultSchema.parse(response)
}

// ── CV search & attach schemas ────────────────────────────────────────────────

const CvSearchResultSchema = z.object({
  libraryId: z.string(),
  sourcepage: z.string(),
  sourcefile: z.string(),
  snippet: z.string().default(""),
  score: z.number().nullable().default(null),
  isFirstChunkForFile: z.boolean().default(false),
})

const CvSearchResponseSchema = z.object({
  results: z.array(CvSearchResultSchema).default([]),
})

const AttachedCvDocumentSchema = z.object({
  id: z.string(),
  filename: z.string(),
  contentType: z.string(),
  sizeKb: z.number(),
  embeddingStatus: z.string(),
  hasMarkdown: z.boolean(),
  uploadedAt: z.string(),
})

const CvAttachResponseSchema = z.object({
  document: AttachedCvDocumentSchema,
  alreadyAttached: z.boolean(),
})

export type CvSearchResultApi = z.infer<typeof CvSearchResultSchema>
export type AttachedCvDocumentApi = z.infer<typeof AttachedCvDocumentSchema>
export type CvAttachResponseApi = z.infer<typeof CvAttachResponseSchema>

// ── CV search & attach API functions ─────────────────────────────────────────

export async function searchCvLibrary(
  caseId: string,
  params: { cvLibraryId: string; query: string; top?: number }
): Promise<CvSearchResultApi[]> {
  const response = await api.post(`/tender/cases/${caseId}/cv-search`, {
    query: params.query,
    cv_library_id: params.cvLibraryId,
    top: params.top ?? 20,
  })
  return CvSearchResponseSchema.parse(response).results
}

export async function attachCvFromLibrary(
  caseId: string,
  runId: string,
  params: { cvLibraryId: string; sourcefile: string }
): Promise<CvAttachResponseApi> {
  const response = await api.post(`/tender/cases/${caseId}/runs/${runId}/cv-attach`, {
    cv_library_id: params.cvLibraryId,
    sourcefile: params.sourcefile,
  })
  return CvAttachResponseSchema.parse(response)
}

export async function uploadCvDocument(
  caseId: string,
  { runId, file }: { runId: string; file: File }
): Promise<TenderCaseApi> {
  const formData = new FormData()
  formData.append("run_id", runId)
  formData.append("doc_type", "cv")
  formData.append("files", file)
  const data = await api.post<TenderCaseApi>(`${BASE}/cases/${caseId}/documents`, formData)
  return TenderCaseSchema.parse(data)
}

// ── Participant API functions ──────────────────────────────────────────────────

export async function listTenderParticipants(caseId: string): Promise<TenderParticipantsResponse> {
  return api.get<never, TenderParticipantsResponse>(`${BASE}/cases/${caseId}/participants`)
}

export async function addTenderParticipant(
  caseId: string,
  participant: TenderParticipant
): Promise<TenderParticipant[]> {
  return api.post<never, TenderParticipant[]>(`${BASE}/cases/${caseId}/participants`, {
    oid: participant.oid,
    displayName: participant.displayName,
    type: participant.type,
    role: participant.role,
  })
}

export async function updateTenderParticipantRole(
  caseId: string,
  participantOid: string,
  role: TenderParticipantRole
): Promise<TenderParticipant[]> {
  return api.patch<never, TenderParticipant[]>(
    `${BASE}/cases/${caseId}/participants/${participantOid}`,
    { role }
  )
}

export async function removeTenderParticipant(
  caseId: string,
  participantOid: string
): Promise<TenderParticipant[]> {
  return api.delete<never, TenderParticipant[]>(
    `${BASE}/cases/${caseId}/participants/${participantOid}`
  )
}

export async function transferTenderOwnership(
  caseId: string,
  newOwnerOid: string
): Promise<TenderCaseApi> {
  const data = await api.post<TenderCaseApi>(`${BASE}/cases/${caseId}/transfer-ownership`, {
    newOwnerOid,
  })
  return TenderCaseSchema.parse(data)
}

// Self-join an open case as an editor. Idempotent server-side: re-joining an
// already-involved case returns the case unchanged. Returns the fresh case so
// callers can read the updated currentUserRole / currentUserAccessSource.
export async function joinTenderCase(caseId: string): Promise<TenderCaseApi> {
  const data = await api.post<TenderCaseApi>(`${BASE}/cases/${caseId}/join`, {})
  return TenderCaseSchema.parse(data)
}

export type TenderHandoffRecipient = { oid: string; displayName: string; email: string }

export type TenderSummaryRecipient = TenderHandoffRecipient & { platformAccess: boolean | null }
export type TenderSummaryPayload = { recipientIds: string[]; note?: string }
export type TenderSummaryPreview = TenderHandoffPreview & {
  recipient: TenderSummaryRecipient
  caseLinkIncluded: boolean
}

export async function searchTenderSummaryRecipients(caseId: string, query: string) {
  const result = await api.get<unknown, { results: TenderSummaryRecipient[] }>(
    `${BASE}/cases/${caseId}/summary-recipients`,
    { params: { q: query } }
  )
  return result.results
}

export async function previewTenderSummaryEmail(caseId: string, payload: TenderSummaryPayload) {
  return api.post<TenderSummaryPayload, TenderSummaryPreview>(
    `${BASE}/cases/${caseId}/summary-email/preview`,
    payload
  )
}

export async function sendTenderSummaryEmail(caseId: string, payload: TenderSummaryPayload) {
  return api.post<
    TenderSummaryPayload,
    { sentRecipientIds: string[]; failedRecipientIds: string[] }
  >(`${BASE}/cases/${caseId}/summary-email`, payload)
}

export type TenderHandoffPayload = {
  mainRecipient: TenderHandoffRecipient
  cc: TenderHandoffRecipient[]
  note?: string
}

export type TenderHandoffCcResponse = {
  recipients: TenderHandoffRecipient[]
  excluded: { oid: string; displayName: string }[]
}

/** Canonical, verified automatic Cc defaults; excluded participants are shown to the sender. */
export async function getTenderHandoffCc(caseId: string) {
  return api.get<never, TenderHandoffCcResponse>(`${BASE}/cases/${caseId}/handoff/cc`)
}

// Hand a case over: transfers ownership to the main recipient (Ansvarig), adds CC
// as participants, flips the case to handed-off, and (best-effort) emails the
// summary from the dedicated handoff mailbox. The handoff always completes;
// `emailSent` is false when the mailbox is disabled/unconfigured or the send failed.
export async function handoffTenderCase(
  caseId: string,
  payload: TenderHandoffPayload
): Promise<{ case: TenderCaseApi; emailSent: boolean }> {
  const data = await api.post<TenderHandoffPayload, TenderCaseApi & { emailSent?: boolean }>(
    `${BASE}/cases/${caseId}/handoff`,
    payload
  )
  return { case: TenderCaseSchema.parse(data), emailSent: data.emailSent !== false }
}

export type TenderHandoffPreviewPayload = {
  layout?: "trimmed" | "full"
  summaryOnly?: boolean
  mainRecipientName?: string
  note?: string
}

const TenderHandoffPreviewSchema = z.object({
  subject: z.string(),
  html: z.string(),
  layout: z.enum(["trimmed", "full"]),
})
export type TenderHandoffPreview = z.infer<typeof TenderHandoffPreviewSchema>

// Render the handoff email exactly as it would be sent, without sending it or
// touching the case. Anyone with access to the case may preview. Omitting
// `layout` uses the tenant's configured summary layout; `html` is the full
// branded document, meant for a sandboxed iframe.
export async function previewTenderHandoffEmail(
  caseId: string,
  payload: TenderHandoffPreviewPayload
): Promise<TenderHandoffPreview> {
  const data = await api.post<TenderHandoffPreviewPayload, unknown>(
    `${BASE}/cases/${caseId}/handoff/preview`,
    payload
  )
  return TenderHandoffPreviewSchema.parse(data)
}

const TenderHandoffConfigSchema = z.object({
  userGroupId: z.string().nullable().optional(),
  userGroupName: z.string().nullable().optional(),
})
export type TenderHandoffConfig = z.infer<typeof TenderHandoffConfigSchema>

export async function getTenderHandoffConfig(): Promise<TenderHandoffConfig> {
  const data = await api.get<TenderHandoffConfig>(`${BASE}/handoff-config`)
  return TenderHandoffConfigSchema.parse(data)
}

export async function leaveTenderCase(caseId: string): Promise<void> {
  await api.post(`${BASE}/cases/${caseId}/leave`, {})
}

export async function markTenderNotificationViewed(caseId: string): Promise<void> {
  await api.post(`${BASE}/cases/${caseId}/mark-notification-viewed`, {})
}
